import { useRef, useSyncExternalStore } from 'react';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { DomainStore } from '~/domain/changes/DomainStore';
import { domainTrace } from '~/domain/diagnostics/domainTrace';
import {
    createStructureSetReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';

interface CacheEntry {
    /**
     * Session identity. Each PatientSession owns its own DomainStore, and a
     * freshly loaded patient starts at version 0 — so the version alone is
     * not unique across a patient swap. Keying on the store reference too
     * prevents serving patient A's read models for patient B (both at v0).
     */
    store: DomainStore;
    version: number;
    items: readonly StructureSetReadModel[];
}

/** Stable no-op subscribe used when there is no active session. */
const NOOP_SUBSCRIBE = (): (() => void) => () => {};

const EMPTY: readonly StructureSetReadModel[] = Object.freeze([]);

/**
 * Returns immutable {@link StructureSetReadModel} projections for every
 * structure set on the current patient. The export modal consumes these
 * read models instead of live `StructureSet` entities so the UI never
 * navigates domain objects directly.
 *
 * Results are cached by the change-tracker version: read models are only
 * rebuilt when a domain mutation bumps the version (e.g. an export-panel
 * edit committed through `useExportEditing`). Tolerates a missing
 * session — the modal can mount before a patient is loaded.
 */
export function useExportStructureSetReadModels(): readonly StructureSetReadModel[] {
    const session = useSessionOrNull();
    const store = session?.context.store;
    const cacheRef = useRef<CacheEntry | null>(null);

    return useSyncExternalStore(store?.subscribe ?? NOOP_SUBSCRIBE, () => {
        if (!session || !store) {
            // Drop the cache so a later session at the same version can't
            // serve the prior patient's stale read models.
            cacheRef.current = null;
            return EMPTY;
        }
        const version = store.getVersion();
        if (
            cacheRef.current &&
            cacheRef.current.store === store &&
            cacheRef.current.version === version
        ) {
            return cacheRef.current.items;
        }
        const items = session.patient.structureSets.map(
            createStructureSetReadModel
        );
        domainTrace.view('useExportStructureSetReadModels miss', {
            version,
            count: items.length,
        });
        cacheRef.current = { store, version, items };
        return items;
    });
}
